import { Link } from 'react-router-dom';
import moment from 'moment-timezone';
import StatusChip from 'components/Table/StatusChip';
import RowTypography from 'components/Table/RowTypography';
import { Box, Stack, Typography } from '@mui/material';
import { ReactComponent as SlaMetIndicator } from '../../assets/sla-met-indicator.svg';
import { ReactComponent as AtRiskIndicator } from '../../assets/at-risk-indicator.svg';
import { ReactComponent as SlaMissedtag } from '../../assets/SLA-Missed-Slatag.svg';
import { ReactComponent as SlaMettag } from '../../assets/SLA-Met-Slatag.svg';
import { ReactComponent as InProgressTag } from '../../assets/In-Progress-Slatag.svg';
import { ReactComponent as NotStartedTag } from '../../assets/Not-Started-Slatag.svg';
import { ReactComponent as EditIcon } from '../../assets/edit-img.svg';
import { ReactComponent as DeleteIcon } from '../../assets/delete-img.svg';
import { ReactComponent as AtRiskBadge } from '../../assets/PepWise2.0/At Risk.svg';
import { ReactComponent as InProgressBadge } from '../../assets/PepWise2.0/In Progress.svg';
import { ReactComponent as NotStartedBadge } from '../../assets/PepWise2.0/Not Started.svg';
import { ReactComponent as SlaMetBadge } from '../../assets/PepWise2.0/SLA Met.svg';
import { ReactComponent as SlaNotMetBadge } from '../../assets/PepWise2.0/SLA Not Met.svg';

export const projectLinkFormatter = (programName: string) => {
  return ({ row }: any) => {
    const today = moment().tz('America/Chicago').format('YYYY-MM-DD');

    return (
      <Link
        to={`/data-pipeline/ibp-dashboard/projects/${row.Program_ID}/${row.Project_Id}/${encodeURIComponent(
          programName,
        )}/${encodeURIComponent(row.Project_Name)}/${today}`}
        state={row}
        title={row.Friendly_Name}
      >
        {row.Project_Name}
      </Link>
    );
  };
};

export const ipDelayedStartFormatter = () => {
  return ({ row }: any) => {
    if (row.IP_Delayed_Start && row.IP_Delayed_Start !== 'NA') {
      return (
        <StatusChip displayNumber status="IP_Delayed_Start" value={row.IP_Delayed_Start || '--'} />
      );
    }

    return <div style={{ textAlign: 'center' }}>--</div>;
  };
};

export const ipLongRunningFormatter = () => {
  return ({ row }: any) => {
    if (row.IP_Long_Running && row.IP_Long_Running !== 'NA') {
      return (
        <StatusChip displayNumber status="IP_Long_Running" value={row.IP_Long_Running || '--'} />
      );
    }

    return <div style={{ textAlign: 'center' }}>--</div>;
  };
};

export const jobsFailedFormatter = () => {
  return ({ row }: any) => {
    if (row.Failed && row.Failed !== 'NA') {
      return <StatusChip displayNumber status="JobsFailed" value={row.Failed || '--'} />;
    }

    return <div style={{ textAlign: 'center' }}>--</div>;
  };
};

export const jobsSuccessFormatter = () => {
  return ({ row }: any) => {
    if (row.Success && row.Success !== 'NA') {
      return <StatusChip displayNumber status="JobsSuccess" value={row.Success || '--'} />;
    }

    return <div style={{ textAlign: 'center' }}>--</div>;
  };
};

export const projectSlaStatusFormatter = () => {
  return ({ row }: any) => {
    const slaStatus = row.SLA_Status;

    if (slaStatus === 'NA' || slaStatus === '--') {
      return <div style={{ textAlign: 'center' }}>{slaStatus}</div>;
    }

    if (slaStatus == '' || slaStatus == null) {
      return '';
    }

    return <StatusChip displayNumber status="ProjectSLAStatus" value={slaStatus} />;
  };
};

const formatSlaTime = (slaTime: any): string => {
  if (slaTime) {
    if (slaTime < 10) {
      return `000${slaTime}`.replace(/(.{2})$/, ':$1');
    } else if (slaTime < 100) {
      return `00${slaTime}`.replace(/(.{2})$/, ':$1');
    } else {
      return slaTime.toString().replace(/(.{2})$/, ':$1');
    }
  }
  return '--';
};

const renderSlaDateTime = (dateValue: string, timeValue: string) => (
  <RowTypography textTransform="capitalize">
    {dateValue} {timeValue}
  </RowTypography>
);

export const slaDateTimeFormatter = () => {
  return ({ row }: any) => {
    // Date
    let dateValue = '';
    if (row.SLA_Date != null && row.SLA_Date > 0) {
      dateValue = moment(row.Schedule_Date ? row.Schedule_Date.split('T')[0] : '')
        .add(row.SLA_Date, 'days')
        .format('MM-DD-YY');
    } else {
      dateValue = moment(row.Schedule_Date ? row.Schedule_Date.split('T')[0] : '').format(
        'MM-DD-YY',
      );
    }

    // Time
    const timeValue = formatSlaTime(row.SLA_Time);

    return renderSlaDateTime(dateValue, timeValue);
  };
};

export const programSlaDateTimeFormatter = () => {
  return ({ row }: any) => {
    // ----- Date calculation -----
    let dateValue = '';

    if (row.SLA_Date != null && row.SLA_Date) {
      dateValue = moment(row.SLA_Date.split('T')[0]).format('MM-DD-YY');
    } else if (row.Schedule_Date) {
      dateValue = moment(row.Schedule_Date.split('T')[0]).format('MM-DD-YY');
    } else {
      dateValue = moment().tz('America/Chicago').format('MM-DD-YY');
    }

    // ----- Time calculation -----
    const timeValue = formatSlaTime(row.SLA_Time);

    return renderSlaDateTime(dateValue, timeValue);
  };
};

interface ImageRendererProps {
  imageUrl: string;
}

const iconMap: Record<string, React.FC> = {
  '../../assets/sla-met-indicator.svg': SlaMetIndicator,
  '../../assets/at-risk-indicator.svg': AtRiskIndicator,
  '../../assets/SLA-Missed-Slatag.svg': SlaMissedtag,
  '../../assets/SLA-Met-Slatag.svg': SlaMettag,
  '../../assets/In-Progress-Slatag.svg': InProgressTag,
  '../../assets/Not-Started-Slatag.svg': NotStartedTag,
  // new tags
  '../../assets/PepWise2.0/At Risk.svg': AtRiskBadge,
  '../../assets/PepWise2.0/In Progress.svg': InProgressBadge,
  '../../assets/PepWise2.0/Not Started.svg': NotStartedBadge,
  '../../assets/PepWise2.0/SLA Met.svg': SlaMetBadge,
  '../../assets/PepWise2.0/SLA Not Met.svg': SlaNotMetBadge,
};

export const ImageRenderer: React.FC<ImageRendererProps> = ({ imageUrl }) => {
  const Icon = iconMap[imageUrl];

  return <Box>{Icon ? <Icon /> : <Typography color="error">Icon not found</Typography>}</Box>;
};

export const projectDetailLinkFormatter = (
  programName?: string,
  startDate?: string,
  endDate?: string,
  dateFilter?: string,
) => {
  return ({ row }: any) => (
    <Link
      to={`/data-pipeline/ibp-dashboard/projects/${row.Program_ID}/${row.Project_Id}/${encodeURIComponent(
        programName ?? '',
      )}/${encodeURIComponent(
        row.Project_Name,
      )}?startingDate=${startDate}&endingDate=${endDate}&dateFilter=${dateFilter}`}
      state={row}
      title={row.Friendly_Name}
    >
      {row.Project_Name}
    </Link>
  );
};

export const actualCompletionTimeFormatter = () => {
  return ({ row }: any) => {
    const actualCompletionTime = row.Actual_Completion_Time;

    if (!actualCompletionTime) {
      return '--';
    }

    const [datePart, timePart] = actualCompletionTime.split('T');
    const [year, month, day] = datePart.split('-');
    const [hour, minute, secondWithMs] = timePart.split(':');
    const [second] = secondWithMs.split('.');

    const formattedDateTime = `${month}-${day}-${year} ${hour}:${minute}:${second}`;
    const hasColorCode = Boolean(row.Color_Code);

    return (
      <RowTypography
        textTransform="capitalize"
        style={hasColorCode ? { color: 'rgba(216, 43, 81, 1)', fontWeight: 'bold' } : undefined}
      >
        {formattedDateTime}
      </RowTypography>
    );
  };
};

export const projectActionIconsFormatter = (isAllowed: boolean, handleAction: Function) => {
  return ({ row }: any) => (
    <Stack direction="row" alignItems="center" height="100%">
      {isAllowed ? (
        <>
          <EditIcon
            style={{ cursor: 'pointer' }}
            title="click to edit"
            onClick={(e) => handleAction('Project', 'edit', row, e)}
          />
          <DeleteIcon
            style={{ cursor: 'pointer' }}
            title="click to delete"
            onClick={(e) => handleAction('Project', 'delete', row, e)}
          />
        </>
      ) : (
        '--'
      )}
    </Stack>
  );
};
